/**
 * AvatarController: owns the player's visible character.
 *
 * Loads the baked player.glb (Mixamo Idle/Walk/Run/Jump) and attaches it under
 * PlayerRig.avatarRoot, sets up an anim component with one state per clip, and
 * exposes facing/visibility for the third-person controller. If the GLB is
 * missing or fails to load, NOTHING is built: the local player is invisible
 * until a retry (applyAvatar resets avatarKey), remote players keep only their
 * DOM nameplate (its anchor is config-driven, never reads the model).
 *
 * The model's feet land on the ground because the rig root sits at eye height
 * plus the controller hover clearance: avatar local Y = -(eyeHeight + groundOffset).
 */

import { AnimData, AnimTrack, Asset, BLEND_NONE, CULLFACE_NONE, Entity, BoundingBox, Mat4, MeshInstance, StandardMaterial, Vec3 } from 'playcanvas';
import type { AnimCurve, Application, GraphNode, Layer, Mesh, MorphInstance, SkinInstance } from 'playcanvas';
import { AVATAR_SIZE_SCALE, type AvatarConfig } from '../config/avatar.config';
import { avatarIds } from '../render/LayerManager';
import { damp, mod } from './math-utils';
import { acquireAvatar } from '../network/AvatarAssetCache';

const FLIGHT_CAMERA_DOWN_PITCH = 35;
const FLIGHT_CAMERA_UP_PITCH = 65;
const FLIGHT_POSE_MIN_PITCH = -15;
const FLIGHT_POSE_MAX_PITCH = 30;
const FLIGHT_MOVE_LEAN = 8;
const FLIGHT_MOVE_LEAN_FADE_PITCH = 25;
const FLIGHT_POSE_DAMPING = 0.995;

interface AnimClipAsset {
    name?: string;
    resource?: unknown;
}

interface AnimTrackResource {
    name?: string;
}

interface AvatarAnimLayer {
    transition: (to: string, time?: number) => void;
    play: (name?: string) => void;
    activeStateProgress?: number | null;
    activeStateCurrentTime?: number;
    activeStateDuration?: number;
}

interface AnimCurvePath {
    entityPath?: string[];
    propertyPath?: string[];
}

interface ContainerResource {
    /** Modern render-component instantiation (PlayCanvas 2.x GLB). */
    instantiateRenderEntity?: (options?: object) => Entity;
    /** Legacy model-component instantiation (also exposes the skin/skeleton). */
    instantiateModelEntity?: (options?: object) => Entity;
    renders?: unknown[];
    animations?: AnimClipAsset[];
}

/** Minimal structural view of a render/model-component mesh instance. */
interface OccluderMeshSource {
    mesh: Mesh;
    node: GraphNode;
    visible: boolean;
    skinInstance: SkinInstance | null;
    morphInstance: MorphInstance | null;
}

let _depthOccluderMaterial: StandardMaterial | null = null;
/** Shared depth-only material (no color writes) for remote-avatar occluder clones. */
function depthOccluderMaterial(): StandardMaterial {
    if (!_depthOccluderMaterial) {
        const mat = new StandardMaterial();
        mat.name = 'AvatarDepthOccluder';
        mat.diffuse.set(0, 0, 0);
        mat.emissive.set(0, 0, 0);
        mat.useLighting = false;
        mat.useSkybox = false;
        mat.useFog = false;
        mat.useTonemap = false;
        // Disable all color writes so the clone is invisible; only depth is written.
        mat.redWrite = false;
        mat.greenWrite = false;
        mat.blueWrite = false;
        mat.alphaWrite = false;
        mat.depthWrite = true;
        mat.depthTest = true;
        // Hair cards / skirt panels can face either direction; occlude from both.
        mat.cull = CULLFACE_NONE;
        mat.update();
        _depthOccluderMaterial = mat;
    }
    return _depthOccluderMaterial;
}

class AvatarController {
    private _app: Application;
    private _config: AvatarConfig;
    private _rig: { avatarRoot: Entity };
    /** The model entity parented under avatarRoot (GLB or placeholder). */
    private _model: Entity | null = null;
    private _modelLocalPosition: Vec3 | null = null;
    private _hips: Entity | null = null;
    private _hipsLocalPosition: Vec3 | null = null;
    /** Anim state names actually available (e.g. ['Idle','Walk','Run','Jump']). */
    private _states = new Set<string>();
    private _facing = 0;       // current yaw (degrees), model space (before offset)
    private _facingTarget = 0; // desired yaw
    private _posePitch = 0;
    private _posePitchTarget = 0;
    private _poseRoll = 0;
    private _visible = true;
    /** load() 已失败/超时：迟到的资产不再挂载（超时后与重试实例双模型）。 */
    private _loadFailed = false;
    /** @debug probe frame counter (throttled Hips-position logging during actions). */
    private _probeFrame = 0;
    private _destroyed = false;
    private _lease?: ReturnType<typeof acquireAvatar>;
    private _materials = new Set<StandardMaterial>();
    /** When set, depth-only clones of the model's meshes are rendered into this layer. */
    private _depthOccluderLayer: Layer | null = null;
    /** Depth-only mesh-instance clones (share mesh/skin/morph with the visible model). */
    private _depthOccluders: MeshInstance[] = [];

    constructor(app: Application, rig: { avatarRoot: Entity }, config: AvatarConfig, private layerIds?: number[]) {
        this._app = app;
        this._rig = rig;
        this._config = config;
    }

    /** Load the GLB. Returns true only if the real GLB was loaded — 失败/超时
     *  返回 false 且不建任何占位：本地形象缺席（调用方 applyAvatar 会重置
     *  avatarKey，等下次 onLoad/传送/面板选择重试），远程只剩头顶名牌。
     *  超时时同步释放租约并置 _loadFailed：引擎资产即使迟到加载也不再挂
     *  模型（_loadGlb 守卫，防超时后与重试实例双模型）。 */
    async load(timeoutMs?: number): Promise<boolean> {
        try {
            await Promise.race([
                this._loadGlb(),
                ...(timeoutMs !== undefined
                    ? [new Promise<never>((_, reject) => setTimeout(
                        () => reject(new Error(`GLB 加载超时（${Math.round(timeoutMs / 1000)}s）`)), timeoutMs))]
                    : []),
            ]);
        } catch (err) {
            if (this._destroyed) return false;
            console.warn('[AvatarController] GLB 加载失败/超时（不显示占位形象）：', err);
            this._loadFailed = true;
            this._lease?.release();
            this._lease = undefined;
            return false;
        }
        if (this._destroyed) return false;
        return !!this._model;
    }

    private async _loadGlb(): Promise<void> {
        const url = this._config.avatarUrl;
        this._lease = acquireAvatar(this._app, url);
        const asset = await this._lease.ready;
        // 已销毁、已挂模型或已超时失败：迟到的资产不再挂载（双模型）。
        if (this._destroyed || this._model || this._loadFailed) return;

        const resource = asset.resource as ContainerResource | undefined;
        if (!resource) {
            throw new Error('GLB resource is empty');
        }
        // PlayCanvas ContainerResource exposes instantiateRenderEntity (2.x) or
        // instantiateModelEntity — NOT a plain instantiate().
        let model: Entity | null = null;
        if (typeof resource.instantiateRenderEntity === 'function') {
            model = resource.instantiateRenderEntity();
        } else if (typeof resource.instantiateModelEntity === 'function') {
            model = resource.instantiateModelEntity();
        }
        if (!model) {
            throw new Error('GLB has no instantiateRenderEntity/instantiateModelEntity — not a model container');
        }
        model.name = 'Avatar';
        this._applyRootTransform();
        this._applyModelTransform(model);
        this._setAvatarLayer(model);
        this._prepareAvatarMaterials(model);
        this._disableMeshCulling(model);
        this._rig.avatarRoot.addChild(model);
        this._autoGroundCalibrate(model);
        this._model = model;
        this._captureModelTransform(model);
        this._captureHips(model);
        this._syncEnabled();

        // Set up the anim component + one state per clip so the state machine
        // can transition/crossFade between Idle/Walk/Run/Jump.
        const clips = resource.animations ?? [];
        if (clips.length) this._setupAnim(model, clips);
    }

    private _setupAnim(model: Entity, clips: AnimClipAsset[]): void {
        if (!model.anim) {
            model.addComponent('anim', { activate: true, speed: 1 });
        }
        const anim = model.anim;
        if (!anim) return;
        const layer = anim.baseLayer ?? anim.addLayer('Base');

        const want: Array<[string, string]> = [
            ['idle', this._config.idleAnim],
            ['walk', this._config.walkAnim],
            ['run', this._config.runAnim],
            ['jump', this._config.jumpAnim]
        ];
        for (const [stateName, clipName] of want) {
            const clip = this._findClip(clips, clipName);
            if (!clip?.resource) {
                console.warn(`[AvatarController] 动画 "${clipName}" 未找到，状态 ${stateName} 回退 idle`);
                continue;
            }
            // loop everything except jump (one-shot)
            const loop = stateName !== 'jump';
            const track = stateName === 'jump' ? clip.resource : this._withoutRootTranslation(clip.resource);
            try {
                layer.assignAnimation(stateName, track as never, 1, loop);
                this._states.add(stateName);
            } catch (e) {
                console.warn(`[AvatarController] assignAnimation(${stateName}) 失败：`, e);
            }
        }
        for (const action of this._config.actionAnims) {
            const clip = this._findClip(clips, action.name);
            if (!clip?.resource) {
                console.warn(`[AvatarController] action animation "${action.name}" not found`);
                continue;
            }
            // "In-place" means no HORIZONTAL drift (freeze X/Z). Vertical motion
            // (Y) must stay natural — Kneel drops, Cheering bobs — otherwise the
            // body freezes at standing height while the legs bend and looks like
            // it's floating. The earlier up-float was NOT from Hips Y translation
            // (deltas are ~3cm, same as walk); it was the runtime clamp pinning Y
            // to the taller bind/T-pose. That clamp is gone now, so keeping Y
            // natural is correct and safe.
            const track = this._withoutRootTranslation(clip.resource, false);
            try {
                layer.assignAnimation(action.name, track as never, 1, action.loop === true);
                this._states.add(action.name);
            } catch (e) {
                console.warn(`[AvatarController] assignAnimation(${action.name}) failed:`, e);
            }
        }
        if (this._states.has('idle')) layer.play('idle');
    }

    private _findClip(clips: AnimClipAsset[], name: string): AnimClipAsset | undefined {
        const lower = name.toLowerCase();
        const namesFor = (c: AnimClipAsset) => [
            c.name,
            (c.resource as AnimTrackResource | undefined)?.name
        ].filter((value): value is string => typeof value === 'string' && value.length > 0);
        return clips.find((c) => namesFor(c).some((clipName) => clipName.toLowerCase() === lower))
            ?? clips.find((c) => namesFor(c).some((clipName) => clipName.toLowerCase().includes(lower)));
    }

    private _withoutRootTranslation(resource: unknown, freezeAll = false): unknown {
        if (!(resource instanceof AnimTrack)) {
            console.warn('[AvatarController] skip root-freeze: resource is not AnimTrack', resource);
            return resource;
        }
        const outputs = resource.outputs.slice();
        let frozenCount = 0;
        for (const curve of resource.curves) {
            const axis = hipsCurveAxis(curve);
            if (!axis) continue;
            const source = outputs[curve.output];
            const components = source.components;
            const data = source.data instanceof Float32Array
                ? new Float32Array(source.data)
                : source.data.slice();
            freezeCurveData(data, components, axis, freezeAll);
            outputs[curve.output] = new AnimData(components, data);
            frozenCount++;
        }
        if (frozenCount === 0) {
            console.warn('[AvatarController] root-freeze found 0 Hips curves for', resource.name, '| freezeAll=' + freezeAll, '| curve paths:', resource.curves.map((c) => JSON.stringify(c.paths)));
        } else {
            console.log('[AvatarController] root-freeze:', resource.name, '| frozen', frozenCount, 'curve(s) | freezeAll=' + freezeAll);
        }
        return new AnimTrack(resource.name, resource.duration, resource.inputs, outputs, resource.curves);
    }

    private _captureModelTransform(model: Entity): void {
        this._modelLocalPosition = model.getLocalPosition().clone();
    }

    /** 落地自校准：不同来源 GLB 的网格脚底相对模型原点高度不一（man 贴 0、
     *  BYG 脚底在原点下方、WXB 偏得更多），模型挂到 eye−(eyeHeight+
     *  groundOffset) 后脚底就不在地面——别人看你浮起/陷地，本地第三人称
     *  与远端同步视图都会错。按绑定姿势的网格局部包围盒（mesh.aabb）把最低点对齐 avatarRoot
     *  原点（=挂载后的脚底/地面）；本地与 RemotePlayers 共用本类，一处
     *  修正两端。config 的 groundOffset 仍作动画级微调叠加（如 woman 因
     *  动画 Hips 下沉需要的 0.1）。偏差 <1cm 视为已贴地，>2m 视为数据
     *  异常，均不动。 */
    private _autoGroundCalibrate(model: Entity): void {
        try {
            this._app.root.syncHierarchy();
            const inv = this._rig.avatarRoot.getWorldTransform().clone().invert();
            const mat = new Mat4();
            const piece = new BoundingBox();
            const bounds = new BoundingBox();
            let has = false;
            for (const render of model.findComponents('render') as unknown as { meshInstances: MeshInstance[] }[]) {
                for (const mi of render.meshInstances ?? []) {
                    // 必须用 mesh.aabb（GLB 解析器按 POSITION 顶点算的纯网格局部
                    // 盒，确定性、不随渲染更新）。mi.aabb 的 getter 在 skinned/
                    // 渲染路径返回的是已乘过 node 世界变换的「世界空间盒」，拿它
                    // 再乘一次相对变换等于双重变换——avatarRoot 挂在
                    // eye−(eyeHeight+groundOffset)（约 −1.5m）时算出 min.y≈−1.5、
                    // shift≈+1.5，恰好落进 <2m 可信区间被误应用，模型整体悬空
                    // 约 1.5m（2026-09-22 真机反馈的悬空根因）。
                    const mesh = (mi as unknown as { mesh?: { aabb: BoundingBox } }).mesh;
                    if (!mesh?.aabb) continue;
                    mat.copy(inv).mul(mi.node.getWorldTransform());
                    piece.setFromTransformedAabb(mesh.aabb, mat);
                    if (!has) { bounds.copy(piece); has = true; } else bounds.add(piece);
                }
            }
            if (!has) return;
            const shift = -bounds.getMin().y;
            if (!Number.isFinite(shift) || Math.abs(shift) < 0.01 || Math.abs(shift) > 2) return;
            const local = model.getLocalPosition();
            model.setLocalPosition(local.x, local.y + shift, local.z);
        } catch (err) {
            console.warn('[AvatarController] 落地自校准失败：', err);
        }
    }

    private _captureHips(model: Entity): void {
        this._hips = model.findByName('Hips') as Entity | null;
        this._hipsLocalPosition = this._hips?.getLocalPosition().clone() ?? null;
        console.log('[AvatarController] captureHips: found=' + !!this._hips + ' restLocal=' + (this._hipsLocalPosition ? this._hipsLocalPosition.toString() : 'null'));
    }

    lockRootMotion(mode: 'locomotion' | 'action' = 'locomotion', actionName?: string | null): void {
        this._lockModelTransform();
        if (this._hips && this._hipsLocalPosition) {
            const p = this._hips.getLocalPosition();
            if (mode === 'action') {
                // The bake froze the Hips translation curve to its first frame, so the
                // action no longer drifts or floats. KEEP the frozen anim Y — do NOT
                // clamp it to bind-pose restLocalY, which is a taller T-pose than the
                // idle pose and floats the body up ~10cm. Pin only X/Z to rest so the
                // character stays in its standing spot.
                this._probeFrame++;
                if (this._probeFrame % 30 === 1) {
                    console.log('[probe] action=' + (actionName ?? '?') +
                        ' appliedY=' + p.y.toFixed(4) + ' bindRestY=' + this._hipsLocalPosition.y.toFixed(4) +
                        ' animX=' + p.x.toFixed(4) + ' animZ=' + p.z.toFixed(4));
                }
                this._hips.setLocalPosition(this._hipsLocalPosition.x, p.y, this._hipsLocalPosition.z);
                return;
            }
            this._hips.setLocalPosition(this._hipsLocalPosition.x, p.y, this._hipsLocalPosition.z);
        }
    }

    /** Apply runtime-owned feet placement and facing to the non-animated root. */
    private _applyRootTransform(): void {
        this._rig.avatarRoot.setLocalPosition(0, -(this._config.eyeHeight + this._config.groundOffset), 0);
        this._applyRootPose();
    }

    /** Apply model-authored scale + rotation offset; animation must not own the parent root. */
    private _applyModelTransform(model: Entity): void {
        const s = this._config.avatarScale * AVATAR_SIZE_SCALE;
        model.setLocalScale(s, s, s);
        model.setLocalPosition(0, 0, 0);
        model.setLocalEulerAngles(0, this._config.avatarRotationOffset, 0);
    }

    private _lockModelTransform(): void {
        if (!this._model || !this._modelLocalPosition) return;
        this._model.setLocalPosition(this._modelLocalPosition.x, this._modelLocalPosition.y, this._modelLocalPosition.z);
        this._model.setLocalEulerAngles(0, this._config.avatarRotationOffset, 0);
        this._applyRootPose();
    }

    private _applyRootPose(): void {
        this._rig.avatarRoot.setLocalEulerAngles(this._posePitch, this._facing, this._poseRoll);
    }

    private _setAvatarLayer(e: Entity): void {
        const ids = this.layerIds ?? avatarIds();
        if (!ids.length) return;
        e.forEach((node) => {
            const n = node as Entity;
            // Covers both render-component (instantiateRenderEntity) and
            // model-component (instantiateModelEntity) GLB instantiations.
            if (n.render) n.render.layers = ids;
            if (n.model) n.model.layers = ids;
        });
    }

/**
 * Skinned mesh instances are frustum-culled against an AABB derived from the
 * PREVIOUS frame's skin matrices, and culling runs before the skin update.
 * On the model's first enable the fresh SkinInstance starts with identity
 * matrices, so body parts whose bind-pose AABB falls outside the frustum get
 * culled — and because culled instances skip the skin update, their AABB
 * never refreshes and they stay culled (self-lock) until the camera moves
 * drastically. That is exactly the "first scene entry → switch to third
 * person → body parts missing" case. One avatar is always near the camera,
 * so culling it buys nothing; disable it.
 */
    private _disableMeshCulling(e: Entity): void {
        e.forEach((node) => {
            const n = node as Entity;
            const meshInstances = (n.render as unknown as { meshInstances?: { cull?: boolean }[] } | undefined)?.meshInstances
                ?? (n.model as unknown as { meshInstances?: { cull?: boolean }[] } | undefined)?.meshInstances;
            for (const mi of meshInstances ?? []) mi.cull = false;
        });
    }

    private _prepareAvatarMaterials(e: Entity): void {
        const clones = new Map<StandardMaterial, StandardMaterial>();
        e.forEach((node) => {
            const n = node as Entity;
            const meshInstances = (n.render as unknown as { meshInstances?: { material: unknown }[] } | undefined)?.meshInstances
                ?? (n.model as unknown as { meshInstances?: { material: unknown }[] } | undefined)?.meshInstances;
            if (!meshInstances) return;
            for (const mi of meshInstances) {
                const mat = mi.material;
                if (!(mat instanceof StandardMaterial)) continue;
                let copy = clones.get(mat);
                if (!copy) {
                    copy = mat.clone() as StandardMaterial;
                    this._materials.add(copy);
                    copy.useSkybox = false;
                    copy.reflectivity = 0;
                    if (isTransparentAvatarMaterial(mat)) {
                        copy.depthTest = true;
                        copy.depthWrite = shouldWriteAvatarAlphaDepth(mat);
                    }
                    copy.update();
                    clones.set(mat, copy);
                }
                mi.material = copy;
            }
        });
    }

    /** Smoothly rotate the character to face `yaw` (world degrees). */
    setFacing(yaw: number, dt: number): void {
        if (!this._model) return;
        this._posePitch = 0;
        this._posePitchTarget = 0;
        this._poseRoll = 0;
        this._facingTarget = yaw;
        if (dt > 0) {
            const t = damp(0.8, dt);
            const diff = mod(this._facingTarget - this._facing + 180, 360) - 180;
            this._facing = mod(this._facing + diff * t, 360);
        } else {
            this._facing = mod(this._facingTarget, 360);
        }
        this._applyRootPose();
    }

    /** Snap facing immediately (no smoothing) — used on spawn/mode switch. */
    snapFacing(yaw: number): void {
        this._posePitch = 0;
        this._posePitchTarget = 0;
        this._poseRoll = 0;
        this._facing = mod(yaw, 360);
        this._facingTarget = this._facing;
        this._applyRootPose();
    }

    /** Map flight pitch continuously, with a small forward/back lean near level flight.
     *  The camera-pitch-driven body tilt only applies while moving; when idle the
     *  character stays upright regardless of where the camera looks. */
    setFlightPose(yaw: number, pitch: number, forwardAmount: number, dt: number, strafeAmount = 0): void {
        if (!this._model) return;
        const moving = Math.abs(forwardAmount) > 1e-4 || Math.abs(strafeAmount) > 1e-4;
        const basePitch = moving ? this._flightPitchToPosePitch(pitch) : 0;
        const flatWeight = 1 - Math.max(0, Math.min(1, Math.abs(pitch) / FLIGHT_MOVE_LEAN_FADE_PITCH));
        const moveLean = Math.max(-1, Math.min(1, forwardAmount)) * FLIGHT_MOVE_LEAN * flatWeight;
        const rootPitch = Math.max(FLIGHT_POSE_MIN_PITCH, Math.min(FLIGHT_POSE_MAX_PITCH, basePitch + moveLean));
        this._facing = mod(yaw, 360);
        this._facingTarget = this._facing;
        this._posePitchTarget = rootPitch;
        const t = dt > 0 ? damp(FLIGHT_POSE_DAMPING, dt) : 1;
        this._posePitch += (this._posePitchTarget - this._posePitch) * t;
        this._poseRoll = 0;
        this._applyRootPose();
    }

    private _flightPitchToPosePitch(pitch: number): number {
        if (pitch < 0) {
            const t = Math.max(-1, Math.min(0, pitch / FLIGHT_CAMERA_DOWN_PITCH));
            return -t * FLIGHT_POSE_MIN_PITCH;
        }
        const t = Math.max(0, Math.min(1, pitch / FLIGHT_CAMERA_UP_PITCH));
        return t * FLIGHT_POSE_MAX_PITCH;
    }

    /** Clear any flight pitch so walk/idle facing remains upright. */
    clearFlightPose(): void {
        this._posePitch = 0;
        this._posePitchTarget = 0;
        this._poseRoll = 0;
        this._applyRootPose();
    }

    setVisible(v: boolean): void {
        if (v === this._visible && this._model) {
            // State unchanged: only keep (possibly freshly built) occluder clones in sync.
            for (const mi of this._depthOccluders) mi.visible = v;
            return;
        }
        this._visible = v;
        this._syncEnabled();
    }

    /** Apply current visibility to the model (model attaches asynchronously).
     *  Hides via meshInstance.visible instead of entity.enabled: disabling a
     *  render component destroys its cached skinInstance (onDisable →
     *  _clearSkinInstances) and re-creates it on enable — while the depth
     *  occluder clones keep sharing the destroyed instance and would crash
     *  the renderer (boneTexture === null in uploadBones). Grid-level hiding
     *  keeps the skin instances alive and lets the anim component keep
     *  running, so poses stay continuous across hide/show cycles. */
    private _syncEnabled(): void {
        if (!this._model) return;
        if (this._depthOccluderLayer && !this._depthOccluders.length) this._buildDepthOccluders();
        for (const mi of this._collectMeshInstances(this._model)) mi.visible = this._visible;
        for (const mi of this._depthOccluders) mi.visible = this._visible;
    }

    /** Render invisible depth-only copies of this avatar's meshes into `layer`.
     *  Used by remote players: the AvatarDepth layer clears the scene depth and
     *  re-writes each remote avatar's true (animated) silhouette, so the local
     *  third-person avatar drawn afterwards is occluded by the remote player's
     *  actual mesh instead of a proxy capsule/rectangle. Call before load(). */
    enableDepthOccluder(layer: Layer): void {
        if (this._depthOccluderLayer === layer) return;
        this._clearDepthOccluders();
        this._depthOccluderLayer = layer;
        if (this._model) this._buildDepthOccluders();
    }

    private _collectMeshInstances(e: Entity): OccluderMeshSource[] {
        const out: OccluderMeshSource[] = [];
        e.forEach((node) => {
            const n = node as Entity;
            const meshInstances = (n.render as unknown as { meshInstances?: OccluderMeshSource[] } | undefined)?.meshInstances
                ?? (n.model as unknown as { meshInstances?: OccluderMeshSource[] } | undefined)?.meshInstances;
            if (meshInstances) out.push(...meshInstances);
        });
        return out;
    }

    private _buildDepthOccluders(): void {
        const layer = this._depthOccluderLayer;
        if (!layer || !this._model || this._depthOccluders.length) return;
        const material = depthOccluderMaterial();
        for (const src of this._collectMeshInstances(this._model)) {
            const clone = new MeshInstance(src.mesh, material, src.node);
            // Share the skin/morph instances so the depth copy follows the exact
            // animated pose of the visible model at no extra animation cost.
            clone.skinInstance = src.skinInstance;
            clone.morphInstance = src.morphInstance;
            clone.castShadow = false;
            clone.cull = false;
            clone.visible = this._visible;
            this._depthOccluders.push(clone);
        }
        if (this._depthOccluders.length) layer.addMeshInstances(this._depthOccluders, true);
    }

    private _clearDepthOccluders(): void {
        const layer = this._depthOccluderLayer;
        if (layer && this._depthOccluders.length) layer.removeMeshInstances(this._depthOccluders, true);
        for (const mi of this._depthOccluders) {
            // Do NOT call mi.destroy(): the clones share skin/morph instances
            // with the visible model. Releasing the mesh refcount is enough;
            // the rest is garbage-collected once nothing references the clone.
            mi.visible = false;
            (mi as unknown as { mesh: Mesh | null }).mesh = null;
        }
        this._depthOccluders.length = 0;
    }

    get visible(): boolean { return this._visible; }

    /** Vertical distance from the rig root (eye height) down to the avatar's feet. */
    get feetDrop(): number {
        return this._config.eyeHeight + this._config.groundOffset;
    }

    /** True when the GLB provided playable anim states. */
    get hasAnim(): boolean { return this._states.size > 0; }

    /** Available anim state names (for the state machine). */
    hasState(name: string): boolean { return this._states.has(name); }

    /** The anim component's base layer, or null (placeholder / load failure). */
    get animLayer(): AvatarAnimLayer | null {
        const anim = this._model?.anim;
        if (!anim) return null;
        const layer = anim.baseLayer;
        return layer ?? null;
    }

    destroy(): void {
        this._destroyed = true;
        this._clearDepthOccluders();
        this._depthOccluderLayer = null;
        if (this._model) {
            this._rig.avatarRoot.removeChild(this._model);
            this._model.destroy();
            this._model = null;
        }
        this._modelLocalPosition = null;
        this._hips = null;
        this._hipsLocalPosition = null;
        for (const material of this._materials) material.destroy();
        this._materials.clear();
        this._lease?.release();
    }
}

/** Which Hips translation axis(es) a curve drives: 'xyz' (interleaved VEC3),
 *  or a single 'x' | 'y' | 'z' (component-wise). null if not a Hips position
 *  curve. Matches by suffix so it works for both "Hips" and "mixamorig:Hips". */
function hipsCurveAxis(curve: AnimCurve): 'x' | 'y' | 'z' | 'xyz' | null {
    const paths = curve.paths as unknown as AnimCurvePath[];
    for (const { entityPath, propertyPath } of paths) {
        if (!entityPath || entityPath.length === 0) continue;
        const node = entityPath[entityPath.length - 1];
        if (!node || !/hips$/i.test(node)) continue;
        if (!propertyPath || propertyPath[0] !== 'localPosition') continue;
        if (propertyPath.length === 1) return 'xyz';
        const a = (propertyPath[1] ?? '').toLowerCase();
        if (a === 'x' || a === 'y' || a === 'z') return a;
    }
    return null;
}

/** Freeze Hips translation in the curve data.
 *  - freezeAll=true (action clips): clamp X/Y/Z to the first-frame value so the
 *    body stays planted (no drift, no float).
 *  - freezeAll=false (locomotion): clamp X/Z only, preserve Y (natural bob).
 *  Handles both interleaved (components>=3) and component-wise (components===1)
 *  curve layouts. */
function freezeCurveData(
    data: Float32Array | number[],
    components: number,
    axis: 'x' | 'y' | 'z' | 'xyz',
    freezeAll: boolean
): void {
    if (components === 1) {
        // Component-wise single-axis curve. For locomotion, keep Y; freeze the rest.
        if (!freezeAll && axis === 'y') return;
        for (let i = 1; i < data.length; i++) data[i] = data[0];
        return;
    }
    if (components < 3) return;
    for (let i = components; i < data.length; i += components) {
        if (freezeAll) {
            data[i] = data[0];
            data[i + 1] = data[1];
            data[i + 2] = data[2];
        } else {
            // Locomotion: freeze X and Z, keep Y (natural vertical bob).
            data[i] = data[0];
            data[i + 2] = data[2];
        }
    }
}

function isTransparentAvatarMaterial(mat: StandardMaterial): boolean {
    return mat.blendType !== BLEND_NONE || mat.opacity < 1 || mat.alphaTest > 0;
}

function shouldWriteAvatarAlphaDepth(mat: StandardMaterial): boolean {
    return mat.alphaTest > 0 && mat.blendType === BLEND_NONE && mat.opacity >= 1;
}

export { AvatarController };
